import {
  RevenueTableData,
  RevenueResponse,
  RevenueAccordionData,
} from 'types/revenue';
import {
  calculatePercentageDifference,
  formatNumberToMoneyString,
} from './utils';
import { isGroupName } from 'config/channelGroups';

interface GroupedRevenueResponse extends RevenueResponse {
  virtualGroups?: string[];
}

const calcPctOfGoal = (current: number, comparison: number): string => {
  if (comparison === 0) return '-';
  const pct = (current / comparison) * 100;
  return pct.toFixed(2) + '%';
};

export const mapRevenueToDailyTableData = (
  revenue: GroupedRevenueResponse,
  expandedGroups: string[] = []
): RevenueTableData => {
  let dailySum = 0;
  let weekRollingAvg = 0;
  let monthRollingAvg = 0;

  const virtualGroups = revenue.virtualGroups || [];
  const channel_data: { [key: string]: { values: string[]; isGroup: boolean; isVirtual: boolean } } = {};

  revenue.channels.forEach((channel, index) => {
    const isVirtual = virtualGroups.includes(channel);
    
    if (!isVirtual) {
      dailySum += Number(revenue.data['Daily'][index]) || 0;
      weekRollingAvg += Number(revenue.data['7-Day Rolling Average'][index]) || 0;
      monthRollingAvg += Number(revenue.data['30-Day Rolling Average'][index]) || 0;
    }

    channel_data[channel] = {
      values: isVirtual
        ? ['-', '-', '-', '-']
        : [
            formatNumberToMoneyString(Number(revenue.data['Daily'][index]) || 0),
            formatNumberToMoneyString(Number(revenue.data['7-Day Rolling Average'][index]) || 0),
            formatNumberToMoneyString(Number(revenue.data['30-Day Rolling Average'][index]) || 0),
            calculatePercentageDifference(
              Number(revenue.data['Daily'][index]) || 0,
              Number(revenue.data['30-Day Rolling Average'][index]) || 0
            ),
          ],
      isGroup: isGroupName(channel),
      isVirtual,
    };
  });

  const comparisonPercentage = calculatePercentageDifference(
    dailySum,
    monthRollingAvg
  );

  return {
    featured_data: {
      Daily: formatNumberToMoneyString(dailySum),
      '7-Day Rolling Average': formatNumberToMoneyString(weekRollingAvg),
      '30-Day Rolling Average': formatNumberToMoneyString(monthRollingAvg),
      '% Daily vs 30-Day Avg': comparisonPercentage,
    },
    channel_data,
  };
};

export const mapRevenueToYearlyTableData = (
  revenue: GroupedRevenueResponse,
  expandedGroups: string[] = [],
  paceDetailsExpanded: boolean = false
): RevenueTableData => {
  let goal = 0;
  let ytdActuals = 0;
  let ytdPace = 0;

  const virtualGroups = revenue.virtualGroups || [];
  const channel_data: { [key: string]: { values: string[]; isGroup: boolean; isVirtual: boolean; paceDetails?: string[] } } = {};

  const hasRawTotals = revenue.data['Last Year Total'] !== undefined;

  const calcPct = (current: number, comparison: number): string => {
    if (comparison === 0) return '-';
    const pct = ((current - comparison) / comparison) * 100;
    return pct.toFixed(2) + '%';
  };

  revenue.channels.forEach((channel, index) => {
    const isVirtual = virtualGroups.includes(channel);
    
    if (!isVirtual) {
      goal += Number(revenue.data['Goal'][index]) || 0;
      ytdActuals += Number(revenue.data['YTD Actuals'][index]) || 0;
      ytdPace += Number(revenue.data['YTD Pace'][index]) || 0;
    }

    const channelGoal = Number(revenue.data['Goal'][index]) || 0;
    const channelPace = Number(revenue.data['YTD Pace'][index]) || 0;

    const baseValues = isVirtual
      ? ['-', '-', '-', '-']
      : [
          formatNumberToMoneyString(channelGoal),
          formatNumberToMoneyString(Number(revenue.data['YTD Actuals'][index]) || 0),
          formatNumberToMoneyString(channelPace),
          calcPctOfGoal(channelPace, channelGoal),
        ];

    let paceDetails: string[] | undefined;
    if (!isVirtual) {
      if (hasRawTotals) {
        const lastYearTotal = Number(revenue.data['Last Year Total']?.[index]) || 0;
        const threeYearAvgTotal = Number(revenue.data['Three Year Avg Total']?.[index]) || 0;

        paceDetails = [
          calcPctOfGoal(channelPace, channelGoal),
          calcPct(channelPace, lastYearTotal),
          calcPct(channelPace, threeYearAvgTotal),
        ];
      } else {
        paceDetails = [
          String(revenue.data['Pace vs Goal']?.[index] || '-'),
          String(revenue.data['Pace vs Last Year']?.[index] || '-'),
          String(revenue.data['Pace vs Three Year Avg']?.[index] || '-'),
        ];
      }
    } else {
      paceDetails = ['-', '-', '-'];
    }

    channel_data[channel] = {
      values: baseValues,
      isGroup: isGroupName(channel),
      isVirtual,
      paceDetails,
    };
  });

  return {
    featured_data: {
      Goal: formatNumberToMoneyString(goal),
      'YTD Actuals': formatNumberToMoneyString(ytdActuals),
      'YTD Pace': formatNumberToMoneyString(ytdPace),
      'Pace vs Goal': calcPctOfGoal(ytdPace, goal),
    },
    channel_data,
  };
};

export const mapRevenueToMonthlyTableData = (
  revenue: GroupedRevenueResponse,
  expandedGroups: string[] = [],
  paceDetailsExpanded: boolean = false
): RevenueTableData => {
  let goal = 0;
  let mtdActuals = 0;
  let mtdPace = 0;

  const virtualGroups = revenue.virtualGroups || [];
  const channel_data: { [key: string]: { values: string[]; isGroup: boolean; isVirtual: boolean; paceDetails?: string[] } } = {};

  const hasPaceDetails = revenue.data['Pace vs Goal'] !== undefined;
  const hasRawTotals = revenue.data['Last Year Total'] !== undefined;

  const calcPct = (current: number, comparison: number): string => {
    if (comparison === 0) return '-';
    const pct = ((current - comparison) / comparison) * 100;
    return pct.toFixed(2) + '%';
  };

  revenue.channels.forEach((channel, index) => {
    const isVirtual = virtualGroups.includes(channel);
    
    if (!isVirtual) {
      goal += Number(revenue.data['Goal'][index]) || 0;
      mtdActuals += Number(revenue.data['MTD Actuals'][index]) || 0;
      mtdPace += Number(revenue.data['MTD Pace'][index]) || 0;
    }

    const channelGoal = Number(revenue.data['Goal'][index]) || 0;
    const channelPace = Number(revenue.data['MTD Pace'][index]) || 0;

    const baseValues = isVirtual
      ? ['-', '-', '-', '-']
      : [
          formatNumberToMoneyString(channelGoal),
          formatNumberToMoneyString(Number(revenue.data['MTD Actuals'][index]) || 0),
          formatNumberToMoneyString(channelPace),
          calcPctOfGoal(channelPace, channelGoal),
        ];

    let paceDetails: string[] | undefined;
    if (!isVirtual) {
      if (hasRawTotals) {
        const lastYearTotal = Number(revenue.data['Last Year Total']?.[index]) || 0;
        const lastMonthTotal = Number(revenue.data['Last Month Total']?.[index]) || 0;
        const yearAvgTotal = Number(revenue.data['Year Avg Total']?.[index]) || 0;

        paceDetails = [
          calcPctOfGoal(channelPace, channelGoal),
          calcPct(channelPace, lastYearTotal),
          calcPct(channelPace, lastMonthTotal),
          calcPct(channelPace, yearAvgTotal),
        ];
      } else if (hasPaceDetails) {
        paceDetails = [
          String(revenue.data['Pace vs Goal']?.[index] || '-'),
          String(revenue.data['Pace vs Last Year']?.[index] || '-'),
          String(revenue.data['Pace vs Last Month']?.[index] || '-'),
          String(revenue.data['Pace vs Year Avg']?.[index] || '-'),
        ];
      }
    } else {
      paceDetails = ['-', '-', '-', '-'];
    }

    channel_data[channel] = {
      values: baseValues,
      isGroup: isGroupName(channel),
      isVirtual,
      paceDetails,
    };
  });

  return {
    featured_data: {
      Goal: formatNumberToMoneyString(goal),
      'MTD Actuals': formatNumberToMoneyString(mtdActuals),
      'MTD Pace': formatNumberToMoneyString(mtdPace),
      'Pace vs Goal': calcPctOfGoal(mtdPace, goal),
    },
    channel_data,
  };
};

export const mapRevenueToDailyAccordionData = (
  revenue: RevenueResponse
): RevenueAccordionData => {
  let dailySum = 0;
  let weekRollingAvg = 0;
  let monthRollingAvg = 0;

  const channels: any = {
    Daily: {},
    '7-Day Rolling Average': {},
    '30-Day Rolling Average': {},
    '% Daily vs 30-Day Avg': {},
  };

  revenue.channels.forEach((channel, index) => {
    dailySum += Number(revenue.data['Daily'][index]) || 0;
    weekRollingAvg += Number(revenue.data['7-Day Rolling Average'][index]) || 0;
    monthRollingAvg += Number(revenue.data['30-Day Rolling Average'][index]) || 0;

    channels.Daily[channel] = formatNumberToMoneyString(
      Number(revenue.data['Daily'][index]) || 0
    );
    channels['7-Day Rolling Average'][channel] = formatNumberToMoneyString(
      Number(revenue.data['7-Day Rolling Average'][index]) || 0
    );
    channels['30-Day Rolling Average'][channel] = formatNumberToMoneyString(
      Number(revenue.data['30-Day Rolling Average'][index]) || 0
    );

    channels['% Daily vs 30-Day Avg'][channel] = calculatePercentageDifference(
      Number(revenue.data['Daily'][index]) || 0,
      Number(revenue.data['30-Day Rolling Average'][index]) || 0
    );
  });

  const comparisonPercentage = calculatePercentageDifference(
    dailySum,
    monthRollingAvg
  );

  return {
    Daily: {
      value: formatNumberToMoneyString(dailySum),
      channels: channels.Daily,
    },
    '7-Day Rolling Average': {
      value: formatNumberToMoneyString(weekRollingAvg),
      channels: channels['7-Day Rolling Average'],
    },
    '30-Day Rolling Average': {
      value: formatNumberToMoneyString(monthRollingAvg),
      channels: channels['30-Day Rolling Average'],
    },
    '% Daily vs 30-Day Avg': {
      value: comparisonPercentage,
      channels: channels['% Daily vs 30-Day Avg'],
    },
  };
};

export const mapRevenueToMonthlyAccordionData = (
  revenue: RevenueResponse
): RevenueAccordionData => {
  let goal = 0;
  let mtdActuals = 0;
  let mtdPace = 0;

  const channels: any = {
    Goal: {},
    'MTD Actuals': {},
    'MTD Pace': {},
    'Pace vs Goal': {},
  };

  revenue.channels.forEach((channel, index) => {
    goal += Number(revenue.data['Goal'][index]) || 0;
    mtdActuals += Number(revenue.data['MTD Actuals'][index]) || 0;
    mtdPace += Number(revenue.data['MTD Pace'][index]) || 0;

    channels.Goal[channel] = formatNumberToMoneyString(
      Number(revenue.data['Goal'][index]) || 0
    );
    channels['MTD Actuals'][channel] = formatNumberToMoneyString(
      Number(revenue.data['MTD Actuals'][index]) || 0
    );
    channels['MTD Pace'][channel] = formatNumberToMoneyString(
      Number(revenue.data['MTD Pace'][index]) || 0
    );

    channels['Pace vs Goal'][channel] = calcPctOfGoal(
      Number(revenue.data['MTD Pace'][index]) || 0,
      Number(revenue.data['Goal'][index]) || 0
    );
  });

  return {
    Goal: {
      value: formatNumberToMoneyString(goal),
      channels: channels.Goal,
    },
    'MTD Actuals': {
      value: formatNumberToMoneyString(mtdActuals),
      channels: channels['MTD Actuals'],
    },
    'MTD Pace': {
      value: formatNumberToMoneyString(mtdPace),
      channels: channels['MTD Pace'],
    },
    'Pace vs Goal': {
      value: calcPctOfGoal(mtdPace, goal),
      channels: channels['Pace vs Goal'],
    },
  };
};

export const mapRevenueToDailySales = (revenue: RevenueResponse): string => {
  let dailySum = 0;

  revenue.channels.forEach((_, index) => {
    dailySum += Number(revenue.data['Daily'][index]) || 0;
  });

  return formatNumberToMoneyString(dailySum);
};
